/**
 * Defensive guard for the React × browser-translation DOM crash
 * (CHAT-MARITACA-27 / CHAT-MARITACA-28, facebook/react#11538).
 *
 * Google Translate and Chrome's built-in page translation replace the text
 * nodes React rendered with <font> wrappers, re-parenting them. React still
 * holds references to the original nodes, so on its next commit it calls
 * removeChild()/insertBefore() on a node translation has since moved, and the
 * browser throws:
 *
 *   NotFoundError: Failed to execute 'removeChild'  on 'Node': ...
 *   NotFoundError: Failed to execute 'insertBefore' on 'Node': ...
 *
 * The stack is 100% react-dom frames (no app code) and the whole UI unmounts.
 *
 * Marking the React root translate="no" (see public/index.html, #859) is only
 * a hint; Chrome ignores it when the user translates the entire page — the
 * Sentry events fire with <html class="translated-ltr">. This patch makes the
 * two mutation methods resilient: when the target node is no longer under the
 * expected parent, fall back to its real parent (removeChild) or append
 * (insertBefore) instead of throwing, so React's reconciliation survives.
 *
 * Idempotent and a no-op outside a DOM environment.
 */
export function installGoogleTranslateGuard(): void {
  if (typeof Node !== 'function' || !Node.prototype) return;

  const proto = Node.prototype as Node & { __translateGuardInstalled?: boolean };
  if (proto.__translateGuardInstalled) return;
  proto.__translateGuardInstalled = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild<T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) {
      // Translation re-parented `child`; remove it from where it actually is
      // (or no-op if it is already detached) rather than throwing.
      if (child.parentNode) {
        return originalRemoveChild.call(child.parentNode, child) as T;
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // The reference node was re-parented by translation; append to `this`
      // instead of throwing.
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
